/**
 * Thin WebRTC adapter for a voice session.
 *
 * Signaling is delegated to `sendOffer` (the EAP proxy); this module never knows
 * gateway URLs or credentials. The full offer is sent once ICE gathering completes
 * so no trickle-ICE endpoint is required.
 */

import { VoiceError, VOICE_ERROR } from './voiceErrors';

/**
 * Cap when no public candidate appears (UDP to STUN blocked). The gateway has a public
 * address and learns ours from connectivity checks, so a host-only offer still connects.
 */
const DEFAULT_ICE_GATHERING_TIMEOUT_MS = 1500;
/**
 * Once a public (srflx/relay) candidate exists the offer is usable; full gathering can
 * stall for seconds on VPN/virtual adapters, so wait only briefly for stragglers.
 */
const PUBLIC_CANDIDATE_GRACE_MS = 300;

/** Server-sent event types we map to visible state; anything else is ignored. */
export const VOICE_EVENT = {
  USER_STARTED_SPEAKING: 'user-started-speaking',
  USER_STOPPED_SPEAKING: 'user-stopped-speaking',
  BOT_STARTED_SPEAKING: 'bot-started-speaking',
  BOT_STOPPED_SPEAKING: 'bot-stopped-speaking',
};

/** Returns the parsed event object (must have a string `type`), or null. */
export const parseVoiceEvent = (raw) => {
  if (typeof raw !== 'string') return null;
  try {
    const msg = JSON.parse(raw);
    return msg && typeof msg === 'object' && typeof msg.type === 'string' ? msg : null;
  } catch {
    return null;
  }
};

const normalizeIceServers = (iceServers) => {
  if (!Array.isArray(iceServers)) return [];
  return iceServers
    .map((s) => (typeof s === 'string' ? { urls: s } : s))
    .filter((s) => s && (typeof s.urls === 'string' || Array.isArray(s.urls)));
};

const waitForIceGathering = (pc, timeoutMs, graceMs = PUBLIC_CANDIDATE_GRACE_MS) =>
  new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    let timer = null;
    let graceTimer = null;
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', onChange);
      pc.removeEventListener('icecandidate', onCandidate);
      if (timer) clearTimeout(timer);
      if (graceTimer) clearTimeout(graceTimer);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    const onCandidate = (event) => {
      const type = event?.candidate?.type || / typ (\w+)/.exec(event?.candidate?.candidate || '')?.[1];
      if (!graceTimer && (type === 'srflx' || type === 'relay')) graceTimer = setTimeout(done, graceMs);
    };
    pc.addEventListener('icegatheringstatechange', onChange);
    pc.addEventListener('icecandidate', onCandidate);
    timer = setTimeout(done, timeoutMs);
  });

export const isWebRtcSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.RTCPeerConnection === 'function' &&
  Boolean(navigator?.mediaDevices?.getUserMedia);

/**
 * @param {object} opts
 * @param {MediaStream} opts.localStream
 * @param {Array} [opts.iceServers]
 * @param {(offer: {sdp: string, type: string}) => Promise<{sdp: string, type: string}>} opts.sendOffer
 * @param {(stream: MediaStream) => void} [opts.onRemoteStream]
 * @param {(state: string) => void} [opts.onConnectionState]
 * @param {(event: {type: string}) => void} [opts.onEvent]
 * @returns {Promise<{ pc: RTCPeerConnection, close: () => void }>}
 */
export async function connectVoice({
  localStream,
  iceServers,
  sendOffer,
  onRemoteStream,
  onConnectionState,
  onEvent,
  iceGatheringTimeoutMs = DEFAULT_ICE_GATHERING_TIMEOUT_MS,
}) {
  let pc;
  try {
    pc = new window.RTCPeerConnection({ iceServers: normalizeIceServers(iceServers) });
  } catch (err) {
    throw new VoiceError(VOICE_ERROR.WEBRTC, err);
  }

  let closed = false;
  const onTrack = (event) => {
    const stream = event.streams?.[0] || new MediaStream([event.track]);
    onRemoteStream?.(stream);
  };
  const onState = () => onConnectionState?.(pc.connectionState);
  pc.addEventListener('track', onTrack);
  pc.addEventListener('connectionstatechange', onState);

  let channel = null;
  const onMessage = (e) => {
    const event = parseVoiceEvent(e?.data);
    if (event) onEvent?.(event);
  };
  if (typeof pc.createDataChannel === 'function') {
    try {
      channel = pc.createDataChannel('chat', { ordered: true });
      channel.addEventListener('message', onMessage);
    } catch {
      channel = null;
    }
  }

  const close = () => {
    if (closed) return;
    closed = true;
    pc.removeEventListener('track', onTrack);
    pc.removeEventListener('connectionstatechange', onState);
    if (channel) {
      channel.removeEventListener('message', onMessage);
      try {
        channel.close();
      } catch {
        /* already closed */
      }
    }
    try {
      pc.getSenders?.().forEach((s) => s.track && s.track.stop());
    } catch {
      /* ignore */
    }
    try {
      pc.close();
    } catch {
      /* ignore */
    }
  };

  try {
    localStream.getAudioTracks().forEach((track) => pc.addTrack(track, localStream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc, iceGatheringTimeoutMs);
    if (closed) throw new VoiceError(VOICE_ERROR.WEBRTC);
    const local = pc.localDescription || offer;
    const answer = await sendOffer({ sdp: local.sdp, type: local.type });
    if (closed) throw new VoiceError(VOICE_ERROR.WEBRTC);
    if (!answer || typeof answer.sdp !== 'string' || typeof answer.type !== 'string') {
      throw new VoiceError(VOICE_ERROR.WEBRTC);
    }
    await pc.setRemoteDescription({ sdp: answer.sdp, type: answer.type });
  } catch (err) {
    close();
    if (err instanceof VoiceError || err?.response || err?.name === 'CanceledError') throw err;
    throw new VoiceError(VOICE_ERROR.WEBRTC, err);
  }

  return { pc, close };
}
